import React, { createContext, useContext, useEffect } from "react";
import useConfirm from "./useConfirm";

const ConfirmDialogContext = createContext(null);

let csrfRefreshDialogHandler = null;
let csrfRefreshDialogVisible = false;
let sessionExpiredDialogHandler = null;
let sessionExpiredDialogVisible = false;

export const promptForCsrfRefresh = async () => {
  if (csrfRefreshDialogVisible || !csrfRefreshDialogHandler) {
    return;
  }

  csrfRefreshDialogVisible = true;
  try {
    await csrfRefreshDialogHandler();
  } finally {
    csrfRefreshDialogVisible = false;
  }
};

export const promptForSessionExpired = async () => {
  if (sessionExpiredDialogVisible || !sessionExpiredDialogHandler) {
    return;
  }

  sessionExpiredDialogVisible = true;
  try {
    await sessionExpiredDialogHandler();
  } finally {
    sessionExpiredDialogVisible = false;
  }
};

export function ConfirmDialogProvider({ children }) {
  const [ConfirmDialog, confirmDialog] = useConfirm();

  useEffect(() => {
    csrfRefreshDialogHandler = async () => {
      const shouldRefresh = await confirmDialog(
        "Refresh required",
        "Your security token is no longer valid. Refresh this page before trying again; your changes have not been saved.",
        true,
        [],
        "Cancel",
        "Refresh"
      );

      if (shouldRefresh) {
        window.location.reload();
      }
    };

    sessionExpiredDialogHandler = async () => {
      const shouldLogIn = await confirmDialog(
        "Session expired",
        "Your session has expired. Please log in again to continue.",
        true,
        [],
        "Cancel",
        "Log in"
      );

      if (shouldLogIn) {
        const returnUrl = encodeURIComponent(
          window.location.pathname + window.location.search
        );
        window.location.href = `/login?session-expired=true&return=${returnUrl}`;
      }
    };

    return () => {
      csrfRefreshDialogHandler = null;
      sessionExpiredDialogHandler = null;
    };
  }, [confirmDialog]);

  return (
    <ConfirmDialogContext.Provider value={confirmDialog}>
      <ConfirmDialog />
      {children}
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  return useContext(ConfirmDialogContext);
}
